import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchProblems } from "../redux/slices/Potd";

const Navbar = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const questions = useSelector((state) => state.Potd.data);
  const userData = useSelector((state) => state.user.user);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    dispatch(fetchProblems());
  }, [dispatch]);

  const controlNavbar = useCallback(() => {
    if (typeof window !== "undefined") {
      if (window.scrollY > lastScrollY && window.scrollY > 100) {
        setIsVisible(false);
        setIsMenuOpen(false);
      } else {
        setIsVisible(true);
      }
      setLastScrollY(window.scrollY);
    }
  }, [lastScrollY]);

  useEffect(() => {
    let hideTimeout;
    const handleScroll = () => {
      controlNavbar();
      clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        if (window.scrollY > 100) setIsVisible(false);
      }, 5000);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(hideTimeout);
    };
  }, [controlNavbar]);

  const scrollUp = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsMenuOpen(false);
  };

  const profilePic = JSON.parse(window.localStorage.getItem("PROFILE_PIC"));

  const getLinkClass = ({ isActive }) => 
    `block px-4 py-2 md:p-0 font-medium transition-all duration-300 ${
      isActive 
        ? "text-blue dark:text-blue" 
        : "text-gray-900 dark:text-white hover:text-blue dark:hover:text-blue hover:bg-gray-50 md:hover:bg-transparent dark:hover:bg-gray-700 md:dark:hover:bg-transparent"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 mx-auto w-full max-w-screen-md border-b md:border-all border-gray-100 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 py-3 shadow-md backdrop-blur-xl md:top-3 md:rounded-3xl lg:max-w-screen-lg transition-transform duration-500 ease-in-out ${
        isVisible ? "translate-y-0" : "-translate-y-[150%]"
      }`}
    >
      <div className="px-4 flex items-center justify-between relative">
        
        {/* LOGO (Left) */}
        <div className="flex shrink-0">
          <NavLink
            to="/"
            onClick={scrollUp}
            className="font-bold text-xl text-blue dark:text-blue flex items-center tracking-tight"
          >
            Senior
          </NavLink>
        </div>

        {/* MOBILE CONTROLS (Right - Visible only on mobile) */}
        <div className="md:hidden flex items-center gap-4">
          {/* Mobile Theme Toggle (Placed near hamburger) */}
          <button
            onClick={props.handleThemeSwitch}
            className="text-gray-700 dark:text-white hover:text-blue transition-colors"
          >
            {props.theme === "dark" ? (
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
            ) : (
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
            )}
          </button>

          {/* Hamburger Menu */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="text-gray-900 dark:text-white focus:outline-none p-1"
          >
            <div className="relative w-6 h-5">
              <span className={`absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? "rotate-45 top-2" : "top-0"}`} />
              <span className={`absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? "opacity-0" : "top-2"}`} />
              <span className={`absolute h-0.5 w-6 bg-current transform transition duration-300 ease-in-out ${isMenuOpen ? "-rotate-45 top-2" : "top-4"}`} />
            </div>
          </button>
        </div>

        {/* NAV LINKS (Center - Desktop) */}
        <nav
          className={`${
            isMenuOpen 
              ? "absolute top-full left-0 right-0 mt-3 bg-white dark:bg-gray-800 shadow-xl rounded-b-2xl border-t border-gray-100 dark:border-gray-700 p-4 opacity-100 translate-y-0" 
              : "absolute top-full left-0 right-0 mt-3 opacity-0 -translate-y-5 pointer-events-none md:pointer-events-auto md:opacity-100 md:translate-y-0 md:static md:mt-0 md:flex md:items-center md:gap-8 md:bg-transparent md:shadow-none md:border-none md:p-0"
          } transition-all duration-300 ease-in-out`}
        >
          <NavLink to="/learn" onClick={scrollUp} className={getLinkClass}>
            CM Sheet
          </NavLink>

          <NavLink to="/OA_learn" onClick={scrollUp} className={getLinkClass}>
            OA
          </NavLink>

          {userData?.isAdmin && (
            <NavLink to="/admin" onClick={scrollUp} className={getLinkClass}>
              Admin
            </NavLink>
          )}
        </nav>

        {/* RIGHT ACTION GROUP (Desktop Only) */}
        {/* Contains Theme Toggle + Login/Profile */}
        <div className="hidden md:flex items-center gap-4">
          
          {/* Desktop Theme Toggle */}
          <button
            onClick={props.handleThemeSwitch}
            className="flex items-center justify-center p-2 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            title="Toggle Theme"
          >
            {props.theme === "dark" ? (
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>
            ) : (
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>
            )}
          </button>

          {/* User Profile / Login */}
          <div className="pl-2 border-l border-gray-200 dark:border-gray-700">
            {userData && Object.keys(userData).length > 0 ? (
              <NavLink
                to="/profile"
                onClick={scrollUp}
                className="flex items-center gap-3 group"
              >
                <div className="text-right hidden lg:block">
                  <span className="block text-xs text-gray-500 dark:text-gray-400">Welcome back,</span>
                  <span className="block text-sm font-semibold text-gray-900 dark:text-white group-hover:text-blue transition-colors">
                    {userData.name}
                  </span>
                </div>
                <img
                  className="rounded-full h-9 w-9 object-cover ring-2 ring-transparent group-hover:ring-blue transition-all"
                  src={profilePic || "https://via.placeholder.com/40"}
                  alt="profile"
                />
              </NavLink>
            ) : (
              <NavLink
                to="/login"
                onClick={scrollUp}
                className="px-5 py-2 text-white bg-blue rounded-lg shadow-sm hover:shadow-blue/30 hover:-translate-y-0.5 transition-all duration-300 font-medium text-sm"
              >
                Login
              </NavLink>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;